import { useEffect, useRef, useState } from 'react';
import reposo from '../assets/alexia/reposo.svg';
import pensando from '../assets/alexia/pensando.svg';
import hecho from '../assets/alexia/hecho.svg';
import preocupada from '../assets/alexia/preocupada.svg';
import sonrisa from '../assets/alexia/sonrisa.svg';
import sorprendida from '../assets/alexia/sorprendida.svg';
import './alexia-avatar.css';

/** Alexia ilustrada: moño bajo, ojos de punto y pendiente de marca. */
export type EstadoAlexia = 'reposo' | 'pensando' | 'hecho' | 'preocupada' | 'sonrisa' | 'sorprendida';

const RETRATO: Record<EstadoAlexia, string> = { reposo, pensando, hecho, preocupada, sonrisa, sorprendida };

export function AlexiaAvatar({ estado = 'reposo' }: { estado?: EstadoAlexia }): JSX.Element {
  return (
    <span className={`alexia-cara is-${estado}`} aria-hidden="true">
      <span className="alexia-cara__marco">
        {/* Los tres retratos montados a la vez: cambiar de estado es un fundido, sin esperar a que cargue. */}
        {(Object.keys(RETRATO) as EstadoAlexia[]).map((e) => (
          <img key={e} className={`alexia-cara__retrato${e === estado ? ' is-visible' : ''}`} src={RETRATO[e]} alt="" draggable={false} />
        ))}
      </span>
      {estado === 'pensando' && <span className="alexia-cara__insignia alexia-cara__puntos"><i /><i /><i /></span>}
      {estado === 'hecho' && (
        <span className="alexia-cara__insignia alexia-cara__check">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5" /></svg>
        </span>
      )}
    </span>
  );
}

/** Lo que dura «hecho» antes de volver a reposo. */
export const HECHO_MS = 1600;

/**
 * Traduce «está trabajando» al estado de la cara: pensando mientras trabaja
 * y, cuando acaba bien, «hecho» un momento. Si acaba con error, muestra preocupación sin celebrar.
 */
export function useEstadoAlexia(trabajando: boolean, fallo = false): EstadoAlexia {
  const [hecho, setHecho] = useState(false);
  const antes = useRef(trabajando);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => {
    const acabaDeTerminar = antes.current && !trabajando;
    antes.current = trabajando;
    if (trabajando || fallo) {
      clearTimeout(timer.current);
      setHecho(false);
    } else if (acabaDeTerminar) {
      setHecho(true);
      timer.current = setTimeout(() => setHecho(false), HECHO_MS);
    }
  }, [trabajando, fallo]);
  useEffect(() => () => clearTimeout(timer.current), []);
  if (trabajando) return 'pensando';
  if (fallo) return 'preocupada';
  return hecho ? 'hecho' : 'reposo';
}
